/**
 * API Configuration
 *
 * In development, Vite proxy handles /api/* requests
 * In production, we use the full API URL
 *
 * Authentication is handled via Supabase JWT tokens.
 *
 * Edge Functions are used for authenticated users when enabled.
 */

import { supabase } from './supabase'

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.wundermemos.com'
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || ''
export const EDGE_FUNCTIONS_URL = `${SUPABASE_URL}/functions/v1`

// Feature flag to enable Edge Functions (set to true when ready)
export const USE_EDGE_FUNCTIONS = true

// Default timeout for API calls (10 seconds)
const DEFAULT_TIMEOUT = 10000

// Helper to construct API URLs
export function apiUrl(path: string): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE_URL}${normalizedPath}`
}

/**
 * Helper to add timeout to a promise
 */
function withTimeout<T>(promise: Promise<T>, ms: number, operation = 'Operation'): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${operation} timed out after ${ms}ms`)), ms)
    )
  ])
}

/**
 * Authenticated fetch helper - adds Authorization header with Supabase token
 *
 * Automatically retrieves the current session token from Supabase and adds
 * it to the Authorization header for API calls.
 *
 * Includes timeout handling to prevent hanging after sleep/wake.
 */
export async function authFetch(
  url: string,
  options: RequestInit & { timeout?: number } = {}
): Promise<Response> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options
  const headers = new Headers(fetchOptions.headers)

  try {
    // Get the Supabase session token with timeout to prevent hanging
    const { data: { session } } = await withTimeout(
      supabase.auth.getSession(),
      3000,
      'Session fetch'
    )
    if (session?.access_token) {
      headers.set('Authorization', `Bearer ${session.access_token}`)
    }
  } catch (e) {
    console.warn('[API] Session fetch timeout, proceeding without auth:', e)
    // Continue without auth header - the request may still work for public endpoints
    // or will fail with 401 which is handled by the caller
  }

  // Create abort controller for fetch timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal
    })
    return response
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Check if Edge Functions should be used
 * Returns true if feature flag is enabled and user is authenticated
 */
export async function shouldUseEdgeFunctions(): Promise<boolean> {
  if (!USE_EDGE_FUNCTIONS) return false
  if (!SUPABASE_URL) return false

  try {
    const { data: { session } } = await supabase.auth.getSession()
    return session !== null
  } catch {
    return false
  }
}

/**
 * Call a Supabase Edge Function
 * Automatically includes the Supabase JWT for authentication
 */
export async function callEdgeFunction<T = unknown>(
  functionName: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
    body?: Record<string, unknown>
    timeout?: number
  } = {}
): Promise<T> {
  const { method = 'POST', body, timeout = DEFAULT_TIMEOUT } = options

  // Get current session for auth
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('No authentication token available')
  }

  const url = `${EDGE_FUNCTIONS_URL}/${functionName}`
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
    }

    if (body) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Edge Function failed with status ${response.status}`)
    }

    return await response.json() as T
  } finally {
    clearTimeout(timeoutId)
  }
}

// Helper to construct asset URLs (for audio files)
export function assetUrl(path: string): string {
  if (!path) return ''
  // If already absolute URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE_URL}${normalizedPath}`
}
